/**
 * Constants for List Types
 * These IDs correspond to the ListTypes table in the database
 * Mirror of api/db/primary/listTypeConstants.js
 */

export const LIST_TYPES = {
  CONTACT: 1,
  CONTACT_WORKING: 2,
  INSTANCE: 3,
  INSTANCE_WORKING: 4,
  ARTWORK: 5,
  ARTWORK_WORKING: 6,
  INSTANCE_IMAGE: 7,
  INSTANCE_IMAGE_WORKING: 8,
  INVOICE: 9,
  INVOICE_WORKING: 10,
  OFFER: 11,
  OFFER_WORKING: 12,
  WAREHOUSE_ITEM: 13,
  WAREHOUSE_ITEM_WORKING: 14,
  ARTWORK_IMAGE: 15,
  ARTWORK_IMAGE_WORKING: 16,
  JOB: 17,
  JOB_WORKING: 18,
  ARTIST: 19,
  ARTIST_WORKING: 20,
  COMPANY: 21,
  COMPANY_WORKING: 22,
  EXHIBITION: 23,
  EXHIBITION_WORKING: 24,
  RESERVATION: 25,
  RESERVATION_WORKING: 26,
  EXHIBITION_IMAGE_WORKING: 27,
 
};

/**
 * Check if a type ID represents an image list
 * @param {number} typeId - The list type ID
 * @returns {boolean}
 */
export const isImageListType = typeId => {
  return [
    LIST_TYPES.INSTANCE_IMAGE,
    LIST_TYPES.INSTANCE_IMAGE_WORKING,
    LIST_TYPES.ARTWORK_IMAGE,
    LIST_TYPES.ARTWORK_IMAGE_WORKING,
    LIST_TYPES.EXHIBITION_IMAGE_WORKING,
  ].includes(typeId);
};

/**
 * Check if a type ID represents a working list
 * @param {number} typeId - The list type ID
 * @returns {boolean}
 */
export const isWorkingListType = typeId => {
  return [
    LIST_TYPES.CONTACT_WORKING,
    LIST_TYPES.INSTANCE_WORKING,
    LIST_TYPES.ARTWORK_WORKING,
    LIST_TYPES.INSTANCE_IMAGE_WORKING,
    LIST_TYPES.INVOICE_WORKING,
    LIST_TYPES.OFFER_WORKING,
    LIST_TYPES.WAREHOUSE_ITEM_WORKING,
    LIST_TYPES.ARTWORK_IMAGE_WORKING,
    LIST_TYPES.JOB_WORKING,
    LIST_TYPES.ARTIST_WORKING,
    LIST_TYPES.COMPANY_WORKING,
    LIST_TYPES.EXHIBITION_WORKING,
    LIST_TYPES.EXHIBITION_IMAGE_WORKING,
  ].includes(typeId);
};

/**
 * Get the display name for a list type
 * @param {number} typeId - The list type ID
 * @returns {string}
 */
export const getListTypeName = typeId => {
  const typeNames = {
    [LIST_TYPES.CONTACT]: "Contact",
    [LIST_TYPES.CONTACT_WORKING]: "Contact (Working)",
    [LIST_TYPES.INSTANCE]: "Instance",
    [LIST_TYPES.INSTANCE_WORKING]: "Instance (Working)",
    [LIST_TYPES.ARTWORK]: "Artwork",
    [LIST_TYPES.ARTWORK_WORKING]: "Artwork (Working)",
    [LIST_TYPES.INSTANCE_IMAGE]: "Instance Image",
    [LIST_TYPES.INSTANCE_IMAGE_WORKING]: "Instance Image (Working)",
    [LIST_TYPES.INVOICE]: "Invoice",
    [LIST_TYPES.INVOICE_WORKING]: "Invoice (Working)",
    [LIST_TYPES.OFFER]: "Offer",
    [LIST_TYPES.OFFER_WORKING]: "Offer (Working)",
    [LIST_TYPES.WAREHOUSE_ITEM]: "Warehouse Item",
    [LIST_TYPES.WAREHOUSE_ITEM_WORKING]: "Warehouse Item (Working)",
    [LIST_TYPES.ARTWORK_IMAGE]: "Artwork Image",
    [LIST_TYPES.ARTWORK_IMAGE_WORKING]: "Artwork Image (Working)",
    [LIST_TYPES.JOB]: "Job",
    [LIST_TYPES.JOB_WORKING]: "Job (Working)",
    [LIST_TYPES.ARTIST]: "Artist",
    [LIST_TYPES.ARTIST_WORKING]: "Artist (Working)",
    [LIST_TYPES.COMPANY]: "Company",
    [LIST_TYPES.COMPANY_WORKING]: "Company (Working)",
    [LIST_TYPES.EXHIBITION]: "Exhibition",
    [LIST_TYPES.EXHIBITION_WORKING]: "Exhibition (Working)",
    [LIST_TYPES.EXHIBITION_IMAGE_WORKING]: "Exhibition Image (Working)",
  };

  return typeNames[typeId] || "Unknown";
};

/**
 * Get the corresponding image list type for an entity list type
 * @param {number} entityTypeId - The entity list type ID
 * @returns {number|null} The corresponding image list type ID or null if not supported
 */
export const getImageListType = entityTypeId => {
  const mapping = {
    [LIST_TYPES.INSTANCE]: LIST_TYPES.INSTANCE_IMAGE,
    [LIST_TYPES.INSTANCE_WORKING]: LIST_TYPES.INSTANCE_IMAGE_WORKING,
    [LIST_TYPES.ARTWORK]: LIST_TYPES.ARTWORK_IMAGE,
    [LIST_TYPES.ARTWORK_WORKING]: LIST_TYPES.ARTWORK_IMAGE_WORKING,
    [LIST_TYPES.EXHIBITION]: LIST_TYPES.EXHIBITION_IMAGE_WORKING,
    [LIST_TYPES.EXHIBITION_WORKING]: LIST_TYPES.EXHIBITION_IMAGE_WORKING,
  };
  return mapping[entityTypeId] || null;
};

/**
 * Get the corresponding entity list type for an image list type
 * @param {number} imageTypeId - The image list type ID
 * @returns {number|null} The corresponding entity list type ID or null if not supported
 */
export const getEntityListType = imageTypeId => {
  const mapping = {
    [LIST_TYPES.INSTANCE_IMAGE]: LIST_TYPES.INSTANCE,
    [LIST_TYPES.INSTANCE_IMAGE_WORKING]: LIST_TYPES.INSTANCE_WORKING,
    [LIST_TYPES.ARTWORK_IMAGE]: LIST_TYPES.ARTWORK,
    [LIST_TYPES.ARTWORK_IMAGE_WORKING]: LIST_TYPES.ARTWORK_WORKING,
    [LIST_TYPES.EXHIBITION_IMAGE_WORKING]: LIST_TYPES.EXHIBITION_WORKING,
  };
  return mapping[imageTypeId] || null;
};

/**
 * Check if a list type can be copied to another list type
 * @param {number} sourceTypeId - The source list type ID
 * @param {number} targetTypeId - The target list type ID (optional)
 * @returns {boolean}
 */
export const canCopyListType = (sourceTypeId, targetTypeId = null) => {
  if (targetTypeId) {
    // Check if source and target are a valid pair
    const imageType = getImageListType(sourceTypeId);
    const entityType = getEntityListType(sourceTypeId);
    return imageType === targetTypeId || entityType === targetTypeId;
  }
  // Check if source type supports copying (has a corresponding type)
  return getImageListType(sourceTypeId) !== null || getEntityListType(sourceTypeId) !== null;
};

